import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Modal } from "react-bootstrap";
import { useModalDismiss } from "./useModalDismiss";

interface Folder {
    id: string;
    name: string;
    parentFolderId: string | null;
}

interface Category {
    id: string;
    name: string;
    accessLevel: string;
}

interface Props {
    pageId: string;
    pageTitle: string;
    categoryId: string;
    currentFolderId: string | null;
    onSave: () => void;
    onCancel: () => void;
}

export default function PageMoveForm({ pageId, pageTitle, categoryId, currentFolderId, onSave, onCancel }: Props) {
    const [categoryIdState, setCategoryIdState] = useState<string>(categoryId);
    const [folderId, setFolderId] = useState<string>(currentFolderId ?? "");
    const [categories, setCategories] = useState<Category[]>([]);
    const [folders, setFolders] = useState<Folder[]>([]);
    const [loadingFolders, setLoadingFolders] = useState(false);
    const [acknowledged, setAcknowledged] = useState(false);
    const [saving, setSaving] = useState(false);
    const { show, dismiss, onExited } = useModalDismiss(onCancel, onSave);

    useEffect(() => {
        axios.get(`/api/manuals/categories`)
            .then(res => {
                if (res.data.success) setCategories(res.data.items);
            })
            .catch(() => { });
    }, []);

    useEffect(() => {
        setLoadingFolders(true);
        axios.get(`/api/manuals/categories/${categoryIdState}/folders`)
            .then(res => {
                if (res.data.success) setFolders(res.data.items);
            })
            .catch(() => { setFolders([]); })
            .finally(() => setLoadingFolders(false));
    }, [categoryIdState]);

    const crossCategory = categoryIdState !== categoryId;
    const noChange = !crossCategory && (folderId || null) === currentFolderId;
    const destination = categories.find(c => c.id === categoryIdState);
    const destinationRestricted = destination?.accessLevel === "restricted";

    function handleCategoryChange(e: React.ChangeEvent<HTMLSelectElement>) {
        setCategoryIdState(e.target.value);
        setFolderId("");
        setAcknowledged(false);
    }

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (noChange || (crossCategory && !acknowledged)) return;
        setSaving(true);
        axios.put(`/api/manuals/pages/${pageId}/move`, {
            categoryId: categoryIdState,
            folderId: folderId || null,
        })
            .then(res => {
                if (res.data.success) {
                    toast.success("Page moved");
                    dismiss("save");
                } else {
                    toast.error(res.data.message || "Failed to move");
                    setSaving(false);
                }
            })
            .catch(err => {
                toast.error(err.response?.data?.message || "Failed to move");
                setSaving(false);
            });
    }

    return (
        <Modal show={show} onHide={() => dismiss("cancel")} onExited={onExited} centered backdropClassName="m-modal-backdrop">
            <form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 17, fontWeight: 600 }}>Move page</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ color: "#64748b", fontSize: 13, marginTop: -4, marginBottom: 16 }}>
                        Moving <strong>{pageTitle}</strong> to another category or folder.
                    </p>
                    <div className="mb-3">
                        <label className="m-label">Category</label>
                        <select
                            className="m-input"
                            value={categoryIdState}
                            onChange={handleCategoryChange}
                        >
                            {categories.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="mb-3">
                        <label className="m-label">Folder</label>
                        <select
                            className="m-input"
                            value={folderId}
                            onChange={e => setFolderId(e.target.value)}
                            disabled={loadingFolders}
                        >
                            <option value="">Root of category</option>
                            {folders.map(f => (
                                <option key={f.id} value={f.id}>{f.name}</option>
                            ))}
                        </select>
                    </div>
                    {crossCategory && (
                        <label
                            style={{
                                display: "flex",
                                gap: 8,
                                alignItems: "flex-start",
                                background: "#fef3c7",
                                border: "1px solid #fde68a",
                                borderRadius: 6,
                                padding: "10px 12px",
                                fontSize: 13,
                                color: "#92400e",
                                cursor: "pointer",
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={acknowledged}
                                onChange={e => setAcknowledged(e.target.checked)}
                                style={{ marginTop: 2 }}
                            />
                            <span>
                                Destination is {destinationRestricted ? "limited-access" : "visible to the whole NHC team"},
                                the content you are moving will change accordingly. Proceed?
                            </span>
                        </label>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="m-btn m-btn-ghost" onClick={() => dismiss("cancel")}>Cancel</button>
                    <button type="submit" className="m-btn m-btn-primary" disabled={saving || noChange || (crossCategory && !acknowledged)}>
                        {saving ? "Moving…" : "Move page"}
                    </button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}
